import { useState, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { ChevronDown, ChevronUp, Tags } from 'lucide-react'
import { MobileColors } from 'styles/mobileTokens'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { MobileNoteEditorDrawer } from './MobileNoteEditorModal'
import {
  MobileTagAssignModal,
  MobileDistributorAssignModal,
  MobileGPOAssignModal,
} from './MobileAssignModal'
import { MobileDraftEmailModal } from './MobileEmail/MobileDraftEmailModal'
import { useNavigate } from 'react-router-dom'
import {
  PiToolbox,
  PiChartPieSlice,
  PiNotepad,
  PiHandshake,
  PiIdentificationCard,
  PiShippingContainer,
  PiHandCoins,
  PiTruck,
  PiPencilSimpleLine,
  PiPaperPlaneTilt,
} from 'react-icons/pi'

const quickNoteSchema = z.object({
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
})

type QuickNoteInput = z.infer<typeof quickNoteSchema>

interface FirstBiteDataDrawerProps {
  chainID?: number
  chainProxyId?: number
  doorProxyId?: number
  chainName?: string
  doorName?: string
  doorAddress?: string
  logo?: string | null
  summaryContent: React.ReactNode[]
  locationDetailsContent: React.ReactNode[]
  reducedHeight?: boolean
}

const DrawerContainer = styled.div<{
  $isOpen: boolean
  $reducedHeight?: boolean
}>`
  position: fixed;
  bottom: 100px; /* Position above 60px footer nav */
  left: 0;
  right: 0;
  background: ${MobileColors.backgroundWhite};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  transition: transform 0.3s ease-in-out;
  transform: translateY(
    ${({ $isOpen, $reducedHeight }) =>
      $isOpen || $reducedHeight ? '40px' : 'calc(100% - 60px)'}
  );
  max-height: ${({ $reducedHeight, $isOpen }) =>
    $reducedHeight && !$isOpen
      ? 'calc((90vh - 60px) * 0.6)'
      : 'calc(90vh - 60px)'}; /* Account for footer nav height */
  display: flex;
  flex-direction: column;
  z-index: 70;
`

const DrawerHeader = styled.div`
  background: #eef5f2; /* Light green background */
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`

const DrawerHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;

  &:active {
    background: rgba(0, 0, 0, 0.05);
  }
`

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #000;
  letter-spacing: 0.75px;
  svg {
    font-size: 14px;
  }
`

const SuggestEditRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 16px 72px;
`

const SuggestEditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: #06512f;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;

  &:active {
    opacity: 0.7;
  }
`

const NoteAndActionsRow = styled.div<{
  $isOpen: boolean
  $reducedHeight: boolean
}>`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  align-items: flex-start;
  background: #054628;
  padding-bottom: 32px;
  ${({ $isOpen, $reducedHeight }) =>
    ($isOpen || $reducedHeight) &&
    'width: 100%; position: fixed; bottom: -6px; padding-bottom: 14px;'}
`

const NoteInputWrapper = styled.div`
  flex: 1;
`

const ActionsButtonWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 80;
`

const DropdownOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(4, 30, 18, 0.5);
  z-index: 75;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

const ActionsButton = styled.button`
  background: #2c6b4e;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 18px;
  }

  &:active {
    background: #245a42;
  }
`

const SimpleNoteInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 6px;
  padding: 10px 12px;
  cursor: text;
  height: 40px;
  transition: all 0.2s;

  &:hover {
    border-color: #2c6b4e;
  }
`

const SimpleNoteInputText = styled.span`
  font-size: 14px;
  font-style: italic;
  color: #c4c4c4;
  flex: 1;
`

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
`

const CompanyHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: ${MobileColors.backgroundWhite};
`

const CompanyLogoContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid ${MobileColors.borderDefault};
`

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const EmptyLogo = styled.div`
  width: 100%;
  height: 100%;
  background-color: #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CompanyName = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 0;
  flex: 1;
`

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${MobileColors.borderDefault};
  background: ${MobileColors.backgroundWhite};
  position: sticky;
  top: 0;
  z-index: 10;
`

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? MobileColors.primary : MobileColors.textSecondary};
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? MobileColors.primary : 'transparent')};
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    background: ${MobileColors.backgroundOverlay};
  }
`

const TabContent = styled.div`
  padding: 16px;
`

export interface FirstBiteDataDrawerHandle {
  open: () => void
  close: () => void
  toggle: () => void
}

export const FirstBiteDataDrawer = forwardRef<
  FirstBiteDataDrawerHandle,
  FirstBiteDataDrawerProps
>(function FirstBiteDataDrawer(
  {
    chainID,
    chainProxyId,
    doorProxyId,
    chainName,
    doorName,
    doorAddress,
    logo,
    summaryContent,
    locationDetailsContent,
    reducedHeight = false,
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'summary' | 'location-details'>(
    'summary'
  )
  const [isNoteExpanded, setIsNoteExpanded] = useState(false)
  const [isManageTagsOpen, setIsManageTagsOpen] = useState(false)
  const [isManageDistributorsOpen, setIsManageDistributorsOpen] =
    useState(false)
  const [isManageGPOsOpen, setIsManageGPOsOpen] = useState(false)
  const [isDraftEmailOpen, setIsDraftEmailOpen] = useState(false)
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false)
  const api = apiService()
  const navigate = useNavigate()

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }))

  // Quick note form
  const { control, handleSubmit, reset } = useForm<QuickNoteInput>({
    defaultValues: {
      note: '',
      attachments: [],
    },
    resolver: zodResolver(quickNoteSchema),
  })

  const noteText = useWatch({ control, name: 'note' })
  const [isSavingNote, setIsSavingNote] = useState(false)

  const onSubmitQuickNote = handleSubmit(async (values) => {
    if (values.note?.trim()) {
      setIsSavingNote(true)
      try {
        await api.createNote(
          chainID,
          chainProxyId,
          values.note || '',
          values.attachments || [],
          doorProxyId
        )
        toast.success('Note saved successfully')
        reset({
          note: '',
          attachments: [],
        })
        setIsNoteExpanded(false) // Collapse after saving
      } catch (error) {
        toast.error('Failed to save note')
      } finally {
        setIsSavingNote(false)
      }
    }
  })

  // Actions dropdown items
  const dropdownItems: IDropdownItem[] = [
    {
      label: 'Create a Deal',
      icon: <PiHandshake size={16} />,
      callback: () => {
        if (chainID) {
          navigate(`/deals/create?chain_id=${chainID}`)
        } else {
          toast.info('Create deal - Coming soon')
        }
      },
    },
    {
      label: 'Create a Note',
      icon: <PiNotepad size={16} />,
      callback: () => {
        setIsNoteExpanded(true)
      },
    },
    {
      label: 'Add a Contact',
      icon: <PiIdentificationCard size={16} />,
      callback: () => {
        if (chainID) {
          navigate(`/contacts/create?chain_id=${chainID}`)
        } else {
          toast.info('Add contact - Coming soon')
        }
      },
    },
    {
      label: 'Send an Email',
      icon: <PiPaperPlaneTilt size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsDraftEmailOpen(true)
        } else {
          toast.info('Add email - Coming soon')
        }
      },
    },
    {
      label: 'Manage Tags',
      icon: <Tags size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageTagsOpen(true)
        } else {
          toast.info('Manage tags - Coming soon')
        }
      },
    },
    {
      label: 'Add Known Distributors',
      icon: <PiTruck size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageDistributorsOpen(true)
        } else {
          toast.info('Add distributor - Coming soon')
        }
      },
    },
    {
      label: 'Add GPO',
      icon: <PiShippingContainer size={16} />,
      callback: () => {
        if (chainProxyId || doorProxyId) {
          setIsManageGPOsOpen(true)
        } else {
          toast.info('Add GPO - Coming soon')
        }
      },
    },
    {
      label: 'Manage Samples',
      icon: <PiHandCoins size={16} />,
      callback: () => {
        if (chainProxyId) {
          const params = new URLSearchParams()
          params.set('contact_company_id', String(chainProxyId))
          if (chainName) {
            params.set('company_name', chainName)
          }
          navigate(`/samples?${params.toString()}`)
        } else {
          toast.info('Manage samples - Coming soon')
        }
      },
    },
  ]

  if (isManageTagsOpen) {
    return (
      <MobileTagAssignModal
        isOpen={isManageTagsOpen}
        onClose={() => setIsManageTagsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={!!doorProxyId}
      />
    )
  }

  if (isManageDistributorsOpen) {
    return (
      <MobileDistributorAssignModal
        isOpen={isManageDistributorsOpen}
        onClose={() => setIsManageDistributorsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={!!doorProxyId}
      />
    )
  }

  if (isManageGPOsOpen) {
    return (
      <MobileGPOAssignModal
        isOpen={isManageGPOsOpen}
        onClose={() => setIsManageGPOsOpen(false)}
        contactCompanyId={chainProxyId || 0}
        doorProxyId={doorProxyId}
        isDoorPage={!!doorProxyId}
      />
    )
  }

  if (isDraftEmailOpen) {
    return (
      <MobileDraftEmailModal
        isOpen={isDraftEmailOpen}
        onClose={() => setIsDraftEmailOpen(false)}
        contactCompanyId={chainProxyId || 0}
        companyName={chainName}
      />
    )
  }

  return (
    <>
      {/* Overlay for Actions dropdown */}
      <DropdownOverlay $isVisible={isActionsDropdownOpen} />

      <DrawerContainer $isOpen={isOpen} $reducedHeight={reducedHeight}>
        <DrawerHeader>
          {/* Clickable header to expand/collapse */}
          <DrawerHandle onClick={() => setIsOpen(!isOpen)}>
            <DrawerTitle>
              {' '}
              <PiChartPieSlice /> FIRST BITE DATA
            </DrawerTitle>
            {isOpen ? (
              <ChevronDown size={20} color="#06512F" />
            ) : (
              <ChevronUp size={20} color="#06512F" />
            )}
          </DrawerHandle>

          {/* Note input and Actions button row */}
          <NoteAndActionsRow $isOpen={isOpen} $reducedHeight={reducedHeight}>
            <NoteInputWrapper>
              <SimpleNoteInput onClick={() => setIsNoteExpanded(true)}>
                <PiNotepad size={18} color="#06512F" />
                <SimpleNoteInputText>Write a note here...</SimpleNoteInputText>
              </SimpleNoteInput>
            </NoteInputWrapper>
            <ActionsButtonWrapper>
              <Dropdown
                items={dropdownItems}
                onOpenChange={setIsActionsDropdownOpen}
                buttonComponent={
                  <ActionsButton
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                  >
                    <PiToolbox />
                    Actions
                  </ActionsButton>
                }
              />
            </ActionsButtonWrapper>
          </NoteAndActionsRow>
        </DrawerHeader>

        {(isOpen || reducedHeight) && (
          <>
            <CompanyHeaderRow>
              <CompanyLogoContainer>
                {logo ? (
                  <CompanyLogo src={logo} alt={chainName || ''} />
                ) : (
                  <EmptyLogo />
                )}
              </CompanyLogoContainer>
              <CompanyName>{chainName}</CompanyName>
            </CompanyHeaderRow>

            <TabContainer>
              <Tab
                $active={activeTab === 'summary'}
                onClick={() => setActiveTab('summary')}
              >
                Summary
              </Tab>
              <Tab
                $active={activeTab === 'location-details'}
                onClick={() => setActiveTab('location-details')}
              >
                Location Details
              </Tab>
            </TabContainer>

            <DrawerContent>
              <TabContent>
                {activeTab === 'summary' && (
                  <div className="space-y-4">{summaryContent}</div>
                )}
                {activeTab === 'location-details' && (
                  <div className="space-y-4">{locationDetailsContent}</div>
                )}
              </TabContent>
              <SuggestEditRow>
                <SuggestEditButton
                  onClick={() =>
                    navigate('suggest-edit', {
                      state: {
                        chainProxyId,
                        chainName,
                        doorProxyId,
                        doorName,
                        doorAddress,
                      },
                    })
                  }
                >
                  <PiPencilSimpleLine size={16} />
                  Suggest an edit
                </SuggestEditButton>
              </SuggestEditRow>
            </DrawerContent>
          </>
        )}

        {/* Note Editor Drawer */}
        <MobileNoteEditorDrawer
          isOpen={isNoteExpanded}
          onClose={() => {
            setIsNoteExpanded(false)
            reset({ note: '', attachments: [] })
          }}
          onSave={onSubmitQuickNote}
          isSaving={isSavingNote}
          control={control}
          noteName="note"
          attachmentsName="attachments"
          hasContent={!!noteText?.trim()}
        />
      </DrawerContainer>
    </>
  )
})
